import { Backdrop, Box, Button, CircularProgress, Grid, Tooltip, Typography } from '@mui/material';
import { API, graphqlOperation } from 'aws-amplify';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useState } from 'react';
import { defaultRowsPerPage } from '../../../../utils/common';
import { dayBookHeadCells } from '../../newReports/data';
import { LedgerTransactionModalv2 } from '../../../LedgerTransaction';
import Listing from '../../../../components/listing';
import { listDayBookTransactionDetails, listDayBookTransactionsCount } from '../../../../graphql';
import { useSelector } from 'react-redux';
import { useReportContext } from '../ReportContext';

const DayBook = () => {
  const dashboardData = useSelector((state) => state?.DashboardDataReducer);
  const { startTimeStamp, endTimeStamp } = dashboardData?.report;
  const { setLoading, loading } = useReportContext();
  const [dayBookData, setDayBookData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [dataCount, setDataCount] = useState(0);
  const [debo, setDebo] = useState({});
  const [showSearch, setShowSearch] = useState(false);
  const [accountID, setAccountID] = useState('');
  function filterValidValues(data) {
    const isValidValue = (value) => {
      if (value === null || value === undefined || value === '') return false;
      if (typeof value === 'object' && !Array.isArray(value)) {
        const keys = Object.keys(value);
        return keys.length > 0 && keys.some((key) => value[key] !== '');
      }
      return true;
    };

    return Object.fromEntries(Object.entries(data).filter(([key, value]) => isValidValue(value)));
  }

  const processObject = (obj) => {
    const result = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];

        // Check if the value is an object (not null or array)
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          const keys = Object.keys(value);

          // Proceed only if the object has more than one key
          if (keys.length > 1) {
            const secondKey = keys[1];

            // Case 1: If the second field's key is "value", update the parent field
            if (secondKey === 'value') {
              obj[key] = value[secondKey];
            }
            // Case 2: If the second field's key is "to"
            else if (secondKey === 'to' || secondKey === 'max') {
              const fromValue = value['from'];
              const toValue = value['to'];
              const minValue = value['min'];
              const maxValue = value['max'];
              // Check if both "from" and "to" fields are present (including empty strings)

              if (fromValue && toValue) {
                // Update only if both are present, even if empty
                obj[key] = { [`${key}from`]: fromValue, [`${key}to`]: toValue };
              } else if (minValue || maxValue) {
                // Update only if both are present, even if empty
                obj[key] = { [`${key}from`]: minValue, [`${key}to`]: maxValue };
              } else {
                obj[key] = { from: '', to: '' };
              }
            }
          }
        }
      }
    }
    return obj;
  };

  const processObject2 = (obj) => {
    const result = {};

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          const keys = Object.keys(value);
          const isFromTo = keys.every((k) => k.endsWith('from') || k.endsWith('to') || k.endsWith('min') || k.endsWith('max'));

          if (isFromTo) {
            for (const innerKey in value) {
              if (value.hasOwnProperty(innerKey)) {
                result[innerKey] = value[innerKey];
              }
            }
          } else {
            result[key] = value;
          }
        } else {
          result[key] = value;
        }
      }
    }
    return result;
  };

  function trimFlatObject(obj) {
    return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value]));
  }

  const trimmed = trimFlatObject(debo);
  const changeObj = processObject(trimmed);
  const newObj = processObject2(changeObj);
  const lastObj = filterValidValues(newObj);

  const groupByVoucherAndTransaction = (data) => {
    const map = new Map(); // key = vouchernumber + transactiontype

    for (const item of data) {
      const key = `${item.vouchernumber}__${item.transactiontype || ''}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        if (a.transactiontypecd !== b.transactiontypecd) {
          return a.transactiontypecd === 'Debit' ? -1 : 1;
        }
        return (a.entryordernumber ?? 0) - (b.entryordernumber ?? 0);
      });
    }
    return Array.from(map, ([groupKey, items]) => [groupKey, items]);
  };

  // Fetch day book data
  const dayBookList = async (reset, from, to) => {
    if (reset) {
      setDayBookData([]);
      setPage(0);
    }
    setLoading(true);
    try {
      const resActive = await API.graphql(
        graphqlOperation(listDayBookTransactionDetails, {
          startdate: startTimeStamp,
          enddate: endTimeStamp,
          rowfrom: from,
          columnname: 'enteredtimestamp',
          ordertype: 'desc',
          limitno: to,
          searcharray: '[]',
          searchmode: 0,
        })
      );

      const resCount = await API.graphql(
        graphqlOperation(listDayBookTransactionsCount, {
          startdate: startTimeStamp,
          enddate: endTimeStamp,
        })
      );

      setDataCount(parseInt(resCount?.data?.listDayBookTransactionsCount[0]?.transactioncount) || 0);
      if (reset) {
        setDayBookData(resActive.data.listDayBookTransactionDetails);
      } else {
        setDayBookData((prevItems) => [...prevItems, ...resActive.data.listDayBookTransactionDetails]);
      }
    } catch (err) {
      console.error('Error fetching day book data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount and when date range changes
  useEffect(() => {
    if (startTimeStamp && endTimeStamp) {
      dayBookList(true, 0, rowsPerPage);
    }
  }, [startTimeStamp, endTimeStamp]);

  // Handle pagination - load more data when needed
  useEffect(() => {
    if (dataCount > dayBookData.length && dayBookData.length > 0) {
      dayBookList(false, dayBookData.length, rowsPerPage);
    }
  }, [rowsPerPage]);

  // Handle data updates from Listing component
  const handleSetBodyData = (newData) => {
    // If it's an array of raw data, set it directly
    if (Array.isArray(newData) && newData.length > 0 && !Array.isArray(newData[0])) {
      setDayBookData(newData);
    } else {
      // If it's already grouped data, extract the items and set
      const flatData = newData.flatMap(([key, items]) => items || []);
      setDayBookData(flatData);
    }
  };

  const groupedData = groupByVoucherAndTransaction(dayBookData);

  return (
    <Grid container sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Grid item xs={12} sm={6} md={4} lg={1.8}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '16px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          Day Book
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6} md={2} lg={0.5}>
        {/* {data && data.length > 1 && ( */}
        <Tooltip title="Search" placement="top">
          <Button
            sx={{
              borderWidth: '3px',
              marginLeft: '5px',
              marginBottom: '4px',
              borderColor: 'primary.main',
              borderStyle: 'solid',
              backgroundColor: showSearch === true && '#f3724a',
              whiteSpace: 'nowrap',
              fontWeight: 'bold',
              '&:active': {
                transform: 'scale(0.92)',
                boxShadow: 'inset 0px 0px 5px rgba(0, 0, 0, 0.4)',
              },
              '&:hover': {
                backgroundColor: showSearch ? '#f3724a' : 'white',
              },
              height: '28px',
              minWidth: '10px',
              width: '30px',
            }}
            variant="outlined"
          >
            <SearchIcon
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowSearch((prev) => !prev);
              }}
              sx={{ fontSize: 20 }}
            />
          </Button>
        </Tooltip>
        {/* )} */}
      </Grid>
      <Box
        sx={{
          '.MuiTableCell-root': {
            padding: '5px',
          },
          marginBottom: '10px',
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <Listing
          bodyData={groupedData}
          setBodyData={handleSetBodyData}
          headData={dayBookHeadCells}
          showSearch={showSearch}
          count={dataCount}
          setAccountID={setAccountID}
          dayBook={true}
          setPage={setPage}
          setDataCount={setDataCount}
          countVariable="transactioncount"
          queryCount={listDayBookTransactionsCount}
          queryCountName="listDayBookTransactionsCount"
          rowsPerPage={rowsPerPage}
          page={page}
          query={listDayBookTransactionDetails}
          startTimeStamp={startTimeStamp}
          endTimeStamp={endTimeStamp}
          queryName="listDayBookTransactionDetails"
          queryParams="enteredtimestamp"
          ordertype="desc"
          debo={debo}
          setDebo={setDebo}
          searchArr={lastObj}
          setRowsPerPage={setRowsPerPage}
        />
      </Box>
      {accountID && <LedgerTransactionModalv2 open={accountID} accountId={accountID} onClose={() => setAccountID('')} />}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Grid>
  );
};

export default DayBook;
